import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Reveal, RevealGroup, Label, HEADING } from "@/components/site/Reveal";
import { PageSection } from "@/components/site/PageLayout";

function SectionHeading({ label, title }: { label: string; title: ReactNode }) {
  return (
    <div className="mb-12 md:mb-16">
      <RevealGroup stagger={70} maxStagger={160}>
        <Label n={label} text="" />
        <h2 className={`mt-6 ${HEADING.h2}`}>{title}</h2>
      </RevealGroup>
    </div>
  );
}

/* ---------- Feature grid (hairline-divided tiles, "Areas of Expertise" / "More than just plan sets") ---------- */

export type FeatureItem = { title: string; description: string };

export function FeatureGrid({
  label,
  title,
  items,
  columns = 4,
  theme = "default",
}: {
  label: string;
  title: ReactNode;
  items: FeatureItem[];
  columns?: 2 | 4;
  theme?: "default" | "surface";
}) {
  return (
    <PageSection theme={theme}>
      <SectionHeading label={label} title={title} />
      <div
        className={`grid gap-px border border-border bg-border sm:grid-cols-2 ${
          columns === 4 ? "lg:grid-cols-4" : "lg:grid-cols-2"
        }`}
      >
        {items.map((item, i) => (
          <Reveal key={item.title} delay={Math.min(i * 60, 240)}>
            <div className="h-full bg-background p-8 md:p-10">
              <div className="font-display text-3xl font-black leading-none text-sun">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-4 font-display text-lg font-bold uppercase leading-tight">
                {item.title}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {item.description}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </PageSection>
  );
}

/* ---------- Deliverables / "Why choose SunSmart" checklist ---------- */

export type DeliverableItem = { title: string; description: string };

export function DeliverablesList({
  label,
  title,
  intro,
  items,
  image,
  imageAlt,
  reverse = false,
}: {
  label: string;
  title: ReactNode;
  intro?: string;
  items: DeliverableItem[];
  image?: string;
  imageAlt?: string;
  reverse?: boolean;
}) {
  return (
    <PageSection>
      <SectionHeading label={label} title={title} />
      <div
        className={`grid gap-16 lg:items-start ${
          image ? "lg:grid-cols-[1fr_1.1fr]" : "lg:grid-cols-1"
        }`}
      >
        {image && (
          <div className={reverse ? "lg:order-2" : ""}>
            <Reveal>
              <div className="overflow-hidden rounded-sm border border-border">
                <img
                  src={image}
                  alt={imageAlt ?? ""}
                  loading="lazy"
                decoding="async"
                width={1200}
                height={900}
                  className="h-full w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        )}
        <div className={reverse ? "lg:order-1" : ""}>
          {intro && (
            <Reveal>
              <p className={HEADING.lead}>{intro}</p>
            </Reveal>
          )}
          <div className="mt-8 grid gap-px border border-border bg-border sm:grid-cols-1">
            {items.map((item, i) => (
              <Reveal key={item.title} delay={Math.min(i * 60, 240)}>
                <div className="bg-background p-6">
                  <div className="font-display text-base font-bold uppercase tracking-wide">
                    {item.title}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </PageSection>
  );
}

/* ---------- Numbered process steps ---------- */

export type ProcessStep = { title: string; description: string };

export function ProcessSteps({
  label,
  title,
  intro,
  steps,
  image,
  imageAlt,
  theme = "default",
}: {
  label: string;
  title: ReactNode;
  intro?: string;
  steps: ProcessStep[];
  image?: string;
  imageAlt?: string;
  theme?: "default" | "surface";
}) {
  return (
    <PageSection theme={theme}>
      <SectionHeading label={label} title={title} />
      <div className={`grid gap-16 ${image ? "lg:grid-cols-[1.1fr_1fr] lg:items-center" : ""}`}>
        {image && (
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={image}
                alt={imageAlt ?? ""}
                loading="lazy"
                decoding="async"
                width={1200}
                height={900}
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
        )}
        <div>
          {intro && (
            <Reveal>
              <p className={HEADING.lead}>{intro}</p>
            </Reveal>
          )}
          <div className={`grid gap-4 ${intro ? "mt-10" : ""}`}>
            {steps.map((step, i) => (
              <Reveal key={step.title} delay={Math.min(i * 60, 240)}>
                <div className="flex gap-6 rounded-sm border border-border bg-surface p-6">
                  <div className="font-display text-4xl font-black leading-none text-sun">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-display text-lg font-bold uppercase">{step.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </PageSection>
  );
}

/* ---------- Spec / stat strip ---------- */

export type StatItem = { n: string; k: string; v: string };

export function StatStrip({
  label,
  title,
  stats,
  theme = "default",
}: {
  label: string;
  title: ReactNode;
  stats: StatItem[];
  theme?: "default" | "surface";
}) {
  return (
    <PageSection theme={theme}>
      <SectionHeading label={label} title={title} />
      <div className="grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={s.n} delay={Math.min(i * 60, 240)}>
            <div className="h-full bg-background p-8 md:p-10">
              <div className="font-display text-5xl font-black leading-none text-sun md:text-6xl">
                {s.n}
              </div>
              <div className="mt-4 font-display text-sm font-bold uppercase tracking-wider">
                {s.k}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.v}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </PageSection>
  );
}

/* ---------- Closing CTA band (image + headline + pill CTA) ---------- */

export function ServiceCta({
  label,
  title,
  intro,
  image,
  imageAlt,
  ctaLabel = "Get started",
}: {
  label: string;
  title: ReactNode;
  intro?: string;
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
}) {
  return (
    <PageSection theme="surface">
      <div className={`grid items-center gap-16 ${image ? "lg:grid-cols-[1fr_1fr]" : ""}`}>
        <div>
          <RevealGroup stagger={70} maxStagger={220}>
            <Label n={label} text="Get started" />
            <h2 className={`mt-6 ${HEADING.h2}`}>{title}</h2>
            {intro && <p className={`mt-6 ${HEADING.lead}`}>{intro}</p>}
            <Link
              to="/contact"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink transition-transform hover:scale-[1.03]"
            >
              {ctaLabel}
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </RevealGroup>
        </div>
        {image && (
          <Reveal delay={100}>
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={image}
                alt={imageAlt ?? ""}
                loading="lazy"
                decoding="async"
                width={1200}
                height={900}
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </div>
          </Reveal>
        )}
      </div>
    </PageSection>
  );
}

/* ---------- Service hub card (image card linking to a detail page) ---------- */

export function ServiceLinkCard({
  tag,
  title,
  description,
  image,
  href,
  large = false,
}: {
  tag: string;
  title: string;
  description: string;
  image: string;
  href: string;
  large?: boolean;
}) {
  return (
    <a
      href={href}
      className="group relative block overflow-hidden rounded-sm border border-border bg-surface"
    >
      <div className={large ? "aspect-[16/10]" : "aspect-[4/3]"}>
        <img
          src={image}
          alt=""
          loading="lazy"
          decoding="async"
          width={800}
          height={600}
          sizes="(max-width: 768px) 100vw, 50vw"
          className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-[1.04] group-hover:opacity-100"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
        <div className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">
          {tag}
        </div>
        <h3 className="font-display text-2xl font-black uppercase leading-tight md:text-3xl">
          {title}
        </h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground/80">
          {description}
        </p>
        <div className="mt-4 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-sun">
          Learn more <span className="transition-transform group-hover:translate-x-1">→</span>
        </div>
      </div>
    </a>
  );
}
